/**
 * Substitution catalog — V105-5-1 / ADR-0075 §6 (D-105-3, D-105-17).
 *
 * One reviewed row per option for moving a provider product (`from_provider_key` /
 * `from_product_id`, '*' = the whole provider) toward a FedRAMP boundary. Each option has a
 * strategy family:
 *
 *   hybrid              keep the platform, move this element onto an authorized service
 *   single_hyperscaler  move onto one hyperscaler's government region
 *   inherit_boundary    run inside another company's authorized boundary (FedStart, Knox,
 *                       Game Warden, ...)
 *   stay_commercial     keep it as a documented MAS-CSO-TPR third party (no target; the row
 *                       carries the TPR template text)
 *
 * Coverage is never copied into a row: `to_offering_id` + `to_product_id` are references and
 * `resolveProductCoverage` answers "is it covered, at what class, as of when" at read time.
 *
 * The table (migration 463) is global reviewed content. Its only writer is
 * `applySubstitutionCatalogChange`, called by `ProposedChangeService.applyToTarget` after an
 * editor approves a `substitution_catalog_entry` proposal (target added by 451a).
 *
 * Unknowns are abstentions, never guesses: a field that no cited source supports is null and
 * named in `abstained_fields`. `verified` needs a primary source, a spike reference
 * (non-stay_commercial) and no abstention on the option's identity or effect; the authoring
 * agent (`source_kind='agent'`) never proposes `verified=true` (ADR-0075 §7).
 */
import { ApplyDeferredError, ContentValidationError } from './framework-requirement-service.js';
import { getProvider, parseProductRef } from '../connectors/providers.js';
import { provenance } from './fedramp-marketplace-service.js';
// ---- code enums (ADR-0075 §6) --------------------------------------------------------------
export const STRATEGY_FAMILIES = ['hybrid', 'single_hyperscaler', 'inherit_boundary', 'stay_commercial'];
/**
 * A Partition verdict (ADR-0072) must name one of these. Owner decision 2026-10-08: moving the
 * data to a substitute is a substitute option, not a partition (`move_data_keep_compute` removed).
 */
export const PARTITION_PATTERNS = [
    'federal_tenants_on_authorized', 'reference_class_only', 'metadata_only', 'customer_owned_provider',
];
export const CODE_CHANGE_PATTERNS = [
    'driver_swap', 'sql_dialect', 'binding_to_client', 'api_client_swap', 'queue_semantics', 'vector_index_swap', 'auth_broker_swap', 'none',
];
export const COST_BANDS = ['low', 'medium', 'high', 'very_high'];
/** Platforms that offer their own authorized boundary to run inside. */
export const BOUNDARY_PLATFORMS = ['fedstart', 'knox', 'game_warden'];
/** Fields an author may abstain on (null value + named in `abstained_fields`). */
export const ABSTAINABLE_FIELDS = [
    'to_offering_id', 'to_product_id', 'partition_patterns', 'objectives_affected', 'ksis_affected', 'code_change_pattern',
    'code_change_aliases', 'cost_band', 'spike_ref', 'tpr_template',
];
/** Abstaining on any of these keeps a row unverified: they say what the option is and what it changes. */
const IDENTITY_FIELDS = new Set(['to_offering_id', 'code_change_pattern', 'cost_band', 'partition_patterns', 'tpr_template']);
const ALIAS_KINDS = ['sdk', 'iac_provider', 'container_image', 'model_tag', 'oauth_app', 'domain', 'wrangler_binding', 'iac_resource'];
const FEDRAMP_ID = /^F[R]?\d{6,}[A-Z]?$/;
const KSI_ID = /^KSI-[A-Z]{3}-[A-Z]{3}$/;
const OPTION_KEY = /^[a-z0-9][a-z0-9-]{1,79}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
/** `sce:<from_provider>/<from_product>:<option_key>` — stable, so a re-proposal updates the same row. */
export const catalogEntryId = (fromProvider, fromProduct, optionKey) => `sce:${fromProvider}/${fromProduct}:${optionKey}`;
const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const json = (v) => (typeof v === 'string' ? JSON.parse(v) : v);
const pick = (p, key) => (p[key] !== undefined ? p[key] : p[`${key}_json`] !== undefined ? json(p[`${key}_json`]) : undefined);
const isStrArray = (v) => Array.isArray(v) && v.every((x) => typeof x === 'string' && x.trim().length > 0);
const oneOf = (list, v) => typeof v === 'string' && list.includes(v);
export function isHttpsUrl(u) {
    try {
        return new URL(u).protocol === 'https:';
    }
    catch {
        return false;
    }
}
/**
 * Validate and normalize a proposed catalog row. Pure: the referential checks that need the
 * database (objectives, KSIs, IaC templates) run in the applier. Throws ContentValidationError.
 */
export function normalizeCatalogEntry(raw, ctx) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        throw new ContentValidationError('proposed_state must be an object');
    const p = raw;
    const bad = (m) => { throw new ContentValidationError(`substitution catalog: ${m}`); };
    const fromProvider = str(p.from_provider_key) ?? bad('from_provider_key is required');
    const fromProduct = str(p.from_product_id) ?? bad('from_product_id is required');
    if (!getProvider(fromProvider))
        bad(`unknown from_provider_key ${fromProvider} (add it to providers.ts first)`);
    if (fromProduct !== '*' && !parseProductRef(`${fromProvider}/${fromProduct}`))
        bad(`${fromProvider} has no product ${fromProduct}`);
    const optionKey = str(p.option_key) ?? bad('option_key is required');
    if (!OPTION_KEY.test(optionKey))
        bad(`option_key ${optionKey} must be lower-case kebab`);
    const entryId = str(p.entry_id) ?? catalogEntryId(fromProvider, fromProduct, optionKey);
    if (entryId !== catalogEntryId(fromProvider, fromProduct, optionKey))
        bad(`entry_id must be ${catalogEntryId(fromProvider, fromProduct, optionKey)}`);
    if (ctx.target_id && ctx.target_id !== entryId)
        bad('entry_id must match target_id');
    const version = str(p.catalog_version) ?? bad('catalog_version is required');
    const family = p.strategy_family;
    if (!oneOf(STRATEGY_FAMILIES, family))
        bad(`strategy_family must be one of ${STRATEGY_FAMILIES.join(', ')}`);
    const fam = family;
    const abstained = pick(p, 'abstained_fields') ?? [];
    if (!isStrArray(abstained) && !(Array.isArray(abstained) && abstained.length === 0))
        bad('abstained_fields must be a list of field names');
    for (const f of abstained)
        if (!oneOf(ABSTAINABLE_FIELDS, f))
            bad(`cannot abstain on ${f}`);
    const abst = new Set(abstained);
    const absent = (f, v) => {
        const empty = v === null || v === undefined;
        if (empty && !abst.has(f))
            bad(`${f} is missing: give it or name it in abstained_fields`);
        if (!empty && abst.has(f))
            bad(`${f} is listed as abstained but has a value`);
        return empty;
    };
    // Target.
    const toProvider = str(p.to_provider_key);
    const toProduct = str(p.to_product_id);
    const toOffering = str(p.to_offering_id);
    const boundary = str(p.boundary_platform);
    if (toProvider && !getProvider(toProvider))
        bad(`unknown to_provider_key ${toProvider}`);
    if (toProvider && toProduct && toProduct !== '*' && !parseProductRef(`${toProvider}/${toProduct}`))
        bad(`${toProvider} has no product ${toProduct}`);
    if (toProduct && !toProvider)
        bad('to_product_id needs to_provider_key');
    if (toOffering && !FEDRAMP_ID.test(toOffering))
        bad(`to_offering_id ${toOffering} is not a FedRAMP id`);
    if (boundary && !oneOf(BOUNDARY_PLATFORMS, boundary))
        bad(`boundary_platform must be one of ${BOUNDARY_PLATFORMS.join(', ')}`);
    if (fam === 'stay_commercial') {
        if (toProvider || toProduct || toOffering || boundary)
            bad('a stay_commercial option has no target');
    }
    else {
        if (fam !== 'inherit_boundary' && !toProvider)
            bad(`a ${fam} option names to_provider_key`);
        if (fam === 'inherit_boundary' && !boundary)
            bad('an inherit_boundary option names boundary_platform');
        if (fam !== 'inherit_boundary' && boundary)
            bad('boundary_platform is only for inherit_boundary');
        // ADR-0075 §6: null only for stay_commercial — or an explicit abstention (unknown, unverified).
        absent('to_offering_id', toOffering);
        if (toProvider)
            absent('to_product_id', toProduct);
    }
    // Partition patterns.
    const ppRaw = pick(p, 'partition_patterns');
    let partition = null;
    if (!absent('partition_patterns', ppRaw)) {
        if (!Array.isArray(ppRaw))
            bad('partition_patterns must be a list');
        partition = ppRaw.map((x) => {
            const o = (x ?? {});
            if (!oneOf(PARTITION_PATTERNS, o.pattern))
                bad(`partition pattern must be one of ${PARTITION_PATTERNS.join(', ')}`);
            const description = str(o.description) ?? bad(`partition pattern ${String(o.pattern)} needs a description`);
            if ('keeps_out_of_scope' in o)
                bad(`partition pattern ${String(o.pattern)}: keeps_out_of_scope is replaced by residual answers (owner decision 2026-10-08)`);
            const r = (o.residual ?? null);
            if (!r || typeof r !== 'object' || Array.isArray(r))
                bad(`partition pattern ${String(o.pattern)} needs its residual answers { q1, q2? }`);
            const extra = Object.keys(r).filter((k) => k !== 'q1' && k !== 'q2');
            if (extra.length)
                bad(`partition pattern ${String(o.pattern)} residual has unknown field ${extra[0]}`);
            if (r.q1 !== 'no' && r.q1 !== 'metadata_only')
                bad(`partition pattern ${String(o.pattern)} residual q1 must be no or metadata_only (a pattern that keeps federal data on the item is not a partition)`);
            let residual;
            if (r.q1 === 'no') {
                if (r.q2 !== 'no' && r.q2 !== 'yes')
                    bad(`partition pattern ${String(o.pattern)} residual q1 = no needs q2 (no | yes)`);
                residual = { q1: 'no', q2: r.q2 };
            }
            else {
                if (r.q2 !== undefined && r.q2 !== null)
                    bad(`partition pattern ${String(o.pattern)} residual q2 is asked only when q1 = no`);
                residual = { q1: 'metadata_only' };
            }
            return { pattern: o.pattern, description, residual };
        });
    }
    // Effect.
    const objRaw = pick(p, 'objectives_affected');
    let objectives = null;
    if (!absent('objectives_affected', objRaw)) {
        const o = (objRaw ?? {});
        const lists = ['gained', 'lost', 'shared'];
        for (const k of lists)
            if (o[k] !== undefined && !(Array.isArray(o[k]) && o[k].every((x) => typeof x === 'string' && x.includes(':'))))
                bad(`objectives_affected.${k} must list objective ids`);
        objectives = { gained: [...(o.gained ?? [])], lost: [...(o.lost ?? [])], shared: [...(o.shared ?? [])] };
    }
    const ksiRaw = pick(p, 'ksis_affected');
    let ksis = null;
    if (!absent('ksis_affected', ksiRaw)) {
        if (!Array.isArray(ksiRaw) || !ksiRaw.every((k) => typeof k === 'string' && KSI_ID.test(k)))
            bad('ksis_affected must list KSI ids (KSI-XXX-YYY)');
        ksis = [...new Set(ksiRaw)].sort();
    }
    // Change.
    const ccp = p.code_change_pattern ?? null;
    if (!absent('code_change_pattern', ccp) && !oneOf(CODE_CHANGE_PATTERNS, ccp))
        bad(`code_change_pattern must be one of ${CODE_CHANGE_PATTERNS.join(', ')}`);
    const aliasesRaw = pick(p, 'code_change_aliases');
    let aliases = null;
    if (!absent('code_change_aliases', aliasesRaw)) {
        if (!Array.isArray(aliasesRaw))
            bad('code_change_aliases must be a list');
        aliases = aliasesRaw.map((x) => {
            const o = (x ?? {});
            if (!oneOf(ALIAS_KINDS, o.kind))
                bad(`code_change alias kind must be one of ${ALIAS_KINDS.join(', ')}`);
            return { kind: o.kind, value: str(o.value) ?? bad('a code_change alias needs a value') };
        });
    }
    const cost = p.cost_band ?? null;
    const costBasis = str(p.cost_basis);
    if (!absent('cost_band', cost)) {
        if (!oneOf(COST_BANDS, cost))
            bad(`cost_band must be one of ${COST_BANDS.join(', ')}`);
        if (!costBasis)
            bad('a cost_band states its basis in cost_basis');
    }
    // Verification and IaC.
    const spike = str(p.spike_ref);
    if (spike && spike.length > 120)
        bad('spike_ref is too long');
    if (spike && !/^spike:[a-z0-9-]+\/[a-z0-9*-]+:[a-z0-9][a-z0-9-]{0,63}$/.test(spike))
        bad('spike_ref must name a spike definition (spike:<provider>/<product>:<key>)');
    if (fam !== 'stay_commercial')
        absent('spike_ref', spike);
    else if (spike)
        bad('a stay_commercial option has no spike');
    const iac = pick(p, 'iac_template_refs') ?? [];
    if (!Array.isArray(iac) || !iac.every((x) => typeof x === 'string' && x.trim()))
        bad('iac_template_refs must list template ids');
    // TPR template (stay_commercial only).
    const tprRaw = pick(p, 'tpr_template');
    let tpr = null;
    if (fam === 'stay_commercial') {
        if (!absent('tpr_template', tprRaw)) {
            const o = (tprRaw ?? {});
            const usage = str(o.usage) ?? bad('tpr_template.usage is required');
            const justification = str(o.justification) ?? bad('tpr_template.justification is required');
            if (!isStrArray(o.mitigations) && !(Array.isArray(o.mitigations) && o.mitigations.length === 0))
                bad('tpr_template.mitigations must be a list');
            if (!isStrArray(o.compensating_controls) && !(Array.isArray(o.compensating_controls) && o.compensating_controls.length === 0))
                bad('tpr_template.compensating_controls must be a list');
            tpr = { usage, justification, mitigations: o.mitigations, compensating_controls: o.compensating_controls, corpus_snapshot_id: str(o.corpus_snapshot_id) };
        }
    }
    else if (tprRaw !== undefined && tprRaw !== null)
        bad('tpr_template is only for stay_commercial');
    // Sources: cite or abstain.
    const srcRaw = pick(p, 'sources');
    if (!Array.isArray(srcRaw) || !srcRaw.length)
        bad('a catalog row cites its sources');
    const sources = srcRaw.map((x) => {
        const o = (x ?? {});
        const id = str(o.id) ?? bad('a source needs an id');
        const url = str(o.url) ?? bad(`source ${id} needs a url`);
        const primary = o.primary === true;
        // Repo documents (GAC Pro's own research) may be cited as context, never as a primary source.
        if (!isHttpsUrl(url) && !(url.startsWith('repo:') && !primary))
            bad(`source ${id} needs an https url (repo: paths only as non-primary context)`);
        const retrieved = str(o.retrieved) ?? bad(`source ${id} needs a retrieved date`);
        if (!DATE.test(retrieved))
            bad(`source ${id} retrieved must be YYYY-MM-DD`);
        const supports = o.supports === undefined ? undefined : (isStrArray(o.supports) ? o.supports : bad(`source ${id} supports must list field names`));
        return {
            id, url, title: str(o.title) ?? bad(`source ${id} needs a title`), publisher: str(o.publisher) ?? bad(`source ${id} needs a publisher`),
            version: str(o.version), retrieved, primary, quote: str(o.quote), ...(supports ? { supports } : {}),
        };
    });
    if (ctx.source_kind === 'agent') {
        // ADR-0075 §7: the agent cites a primary source with a quote span for every field it fills.
        for (const s of sources)
            if (!s.primary || !s.quote || !isHttpsUrl(s.url))
                bad(`agent source ${s.id} must be a primary https source with a quote`);
    }
    // Verified: primary source + spike + no abstention on identity; never proposed by the agent.
    const verified = p.verified === true || p.verified === 1;
    if (verified) {
        if (ctx.source_kind === 'agent')
            bad('the authoring agent never proposes verified=true; a reviewer sets it');
        if (!sources.some((s) => s.primary))
            bad('verified needs a primary source');
        for (const f of abst)
            if (IDENTITY_FIELDS.has(f))
                bad(`verified cannot abstain on ${f}`);
        if (fam !== 'stay_commercial' && !spike)
            bad('verified needs a spike_ref (ADR-0074)');
    }
    return {
        entry_id: entryId, catalog_version: version, verified, from_provider_key: fromProvider, from_product_id: fromProduct, option_key: optionKey,
        strategy_family: fam, to_provider_key: toProvider, to_product_id: toProvider ? toProduct : null, to_offering_id: toOffering,
        boundary_platform: boundary ?? null, partition_patterns: partition, objectives_affected: objectives,
        ksis_affected: ksis, code_change_pattern: ccp ?? null, code_change_aliases: aliases,
        cost_band: cost ?? null, cost_basis: cost ? costBasis : null, spike_ref: spike,
        iac_template_refs: [...iac].map((x) => x.trim()), tpr_template: tpr, note: str(p.note), sources,
        abstained_fields: [...abst].sort(),
    };
}
// ---- applier --------------------------------------------------------------------------------
/** `substitution_catalog_entry` → substitution_catalog_entries (upsert; deprecate soft-deletes). */
export async function applySubstitutionCatalogChange(db, change, reviewer) {
    const prov = provenance(change, reviewer);
    const now = new Date().toISOString();
    if (change.change_type === 'deprecate') {
        if (!change.target_id)
            throw new ContentValidationError('deprecate requires target_id');
        await db.execute(`UPDATE substitution_catalog_entries SET status = 'deprecated', reviewer = ?, reviewed_at = ?, updated_at = ? WHERE entry_id = ?`, [prov.reviewer, prov.reviewed_at, now, change.target_id]);
        return change.target_id;
    }
    const raw = typeof change.proposed_state === 'string' ? JSON.parse(change.proposed_state) : change.proposed_state;
    const e = normalizeCatalogEntry(raw, { source_kind: change.source_kind, target_id: change.target_id });
    // Referential checks: content still in the queue defers (ApplyDeferredError), never fails.
    const objectiveIds = e.objectives_affected ? [...new Set([...e.objectives_affected.gained, ...e.objectives_affected.lost, ...e.objectives_affected.shared])] : [];
    for (const id of objectiveIds) {
        const r = await db.query(`SELECT 1 AS ok FROM assessment_objectives WHERE objective_id = ? LIMIT 1`, [id]);
        if (!r.results.length)
            throw new ApplyDeferredError(`objective ${id} is not applied yet`);
    }
    for (const k of e.ksis_affected ?? []) {
        const r = await db.query(`SELECT 1 AS ok FROM framework_requirements WHERE requirement_id = ? LIMIT 1`, [`fedramp-cr26:${k}`]);
        if (!r.results.length)
            throw new ApplyDeferredError(`KSI ${k} is not applied yet`);
    }
    // ADR-0074 owner decision 2026-10-09: spike_ref names a reviewed spike definition (470); one still in the queue defers.
    if (e.spike_ref) {
        const r = await db.query(`SELECT 1 AS ok FROM spike_definitions WHERE spike_ref = ? AND status = 'active' LIMIT 1`, [e.spike_ref]).catch(() => ({ results: [] }));
        if (!r.results.length)
            throw new ApplyDeferredError(`spike definition ${e.spike_ref} is not applied yet`);
    }
    // ADR-0054: IaC is a reviewed template, never model text. A ref that doesn't resolve defers.
    for (const ref of e.iac_template_refs) {
        // ADR-0074 O1: a reviewed IaC template (466b), or an approved cloud_target_mappings template (ADR-0054).
        const t = await db.query(`SELECT 1 AS ok FROM iac_templates WHERE template_id = ? AND status = 'active' LIMIT 1`, [ref]).catch(() => ({ results: [] }));
        const r = t.results.length ? t : await db.query(`SELECT 1 AS ok FROM cloud_target_mappings WHERE mapping_id = ? LIMIT 1`, [ref]);
        if (!r.results.length)
            throw new ApplyDeferredError(`IaC template ${ref} does not resolve to a reviewed template`);
    }
    const j = (v) => (v === null ? null : JSON.stringify(v));
    await db.execute(`INSERT INTO substitution_catalog_entries (
       entry_id, catalog_version, status, verified, from_provider_key, from_product_id, option_key, strategy_family,
       to_provider_key, to_product_id, to_offering_id, boundary_platform, partition_patterns_json, objectives_affected_json,
       ksis_affected_json, code_change_pattern, code_change_aliases_json, cost_band, cost_basis, spike_ref, iac_template_refs_json,
       tpr_template_json, note, sources_json, abstained_fields_json, source_kind, reviewer, reviewed_at, created_at, updated_at)
     VALUES (?, ?, 'active', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
     ON CONFLICT (entry_id) DO UPDATE SET
       catalog_version = excluded.catalog_version, status = 'active', verified = excluded.verified,
       strategy_family = excluded.strategy_family, to_provider_key = excluded.to_provider_key, to_product_id = excluded.to_product_id,
       to_offering_id = excluded.to_offering_id, boundary_platform = excluded.boundary_platform,
       partition_patterns_json = excluded.partition_patterns_json, objectives_affected_json = excluded.objectives_affected_json,
       ksis_affected_json = excluded.ksis_affected_json, code_change_pattern = excluded.code_change_pattern,
       code_change_aliases_json = excluded.code_change_aliases_json, cost_band = excluded.cost_band, cost_basis = excluded.cost_basis,
       spike_ref = excluded.spike_ref, iac_template_refs_json = excluded.iac_template_refs_json, tpr_template_json = excluded.tpr_template_json,
       note = excluded.note, sources_json = excluded.sources_json, abstained_fields_json = excluded.abstained_fields_json,
       source_kind = excluded.source_kind, reviewer = excluded.reviewer, reviewed_at = excluded.reviewed_at, updated_at = excluded.updated_at`, [
        e.entry_id, e.catalog_version, e.verified ? 1 : 0, e.from_provider_key, e.from_product_id, e.option_key, e.strategy_family,
        e.to_provider_key, e.to_product_id, e.to_offering_id, e.boundary_platform, JSON.stringify(e.partition_patterns ?? []),
        JSON.stringify(e.objectives_affected ?? { gained: [], lost: [], shared: [] }), JSON.stringify(e.ksis_affected ?? []),
        e.code_change_pattern, JSON.stringify(e.code_change_aliases ?? []), e.cost_band, e.cost_basis, e.spike_ref,
        JSON.stringify(e.iac_template_refs), j(e.tpr_template), e.note, JSON.stringify(e.sources), JSON.stringify(e.abstained_fields),
        change.source_kind, prov.reviewer, prov.reviewed_at, now, now,
    ]);
    return e.entry_id;
}
/**
 * Active options for a product, with the whole-provider ('*') options after the product's own.
 * Global reviewed content: any member may read it (ADR-0075 §10).
 */
export async function listCatalogOptions(db, providerKey, productId) {
    return (await db.query(`SELECT entry_id, from_provider_key, from_product_id, option_key, strategy_family, to_provider_key, to_product_id, to_offering_id,
            boundary_platform, verified, abstained_fields_json
       FROM substitution_catalog_entries
      WHERE status = 'active' AND from_provider_key = ? AND (from_product_id = ? OR from_product_id = '*')
      ORDER BY CASE WHEN from_product_id = '*' THEN 1 ELSE 0 END, strategy_family, option_key`, [providerKey, productId])).results;
}
//# sourceMappingURL=substitution-catalog-service.js.map